/**
 * A collapsible section.
 *
 * Built on <details> rather than a div and a boolean: the browser gives us
 * the correct semantics, keyboard behaviour and -- in browsers that support
 * `hidden=until-found` on the content -- ctrl-F reaching text inside a
 * closed one. On this app that matters, because searching a statement is
 * how people actually search one.
 */
import { ChevronRight } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { cn } from '~/lib/cn.ts'

export function Disclosure({
  summary, children, defaultOpen = false, open: openProp, onOpenChange,
  className, right, count, tone = 'muted',
}: {
  summary: ReactNode
  children: ReactNode
  defaultOpen?: boolean
  /** Controlled mode. Omit for uncontrolled. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  className?: string
  right?: ReactNode
  count?: number | null
  tone?: 'muted' | 'strong'
}) {
  const [uncontrolled, setUncontrolled] = useState(defaultOpen)
  const controlled = openProp !== undefined
  const open = controlled ? openProp : uncontrolled

  const setOpen = (next: boolean) => {
    if (!controlled) setUncontrolled(next)
    onOpenChange?.(next)
  }

  return (
    <details
      open={open}
      className={cn('group', className)}
      onToggle={(e) => {
        const next = (e.currentTarget as HTMLDetailsElement).open
        if (next !== open) setOpen(next)
      }}
    >
      <summary
        className={cn(
          'tap-row flex cursor-default list-none items-center gap-1.5 py-1',
          'rounded-[var(--radius-control)] transition-colors duration-75',
          'active:bg-[var(--bg-sunken)]',
          'text-[length:var(--text-data)] [&::-webkit-details-marker]:hidden',
          tone === 'strong'
            ? 'font-medium text-[var(--text)]'
            : 'text-[var(--text-muted)] hover:text-[var(--text)]',
        )}
      >
        <ChevronRight
          size={14}
          aria-hidden="true"
          className={cn(
            'shrink-0 transition-transform duration-100',
            'rtl:-scale-x-100',
            open && 'rotate-90',
          )}
        />
        <span className="min-w-0 flex-1 truncate">{summary}</span>
        {count !== null && count !== undefined && (
          <span className="tnum shrink-0 text-[length:var(--text-micro)] text-[var(--text-faint)]">
            {count}
          </span>
        )}
        {right}
      </summary>
      <div className="pt-1">{children}</div>
    </details>
  )
}
